import React from 'react';
import Button from '../ui/Button';
import Avatar from '../ui/Avatar';
import { StatusDot } from '../ui/Status';
import './FocusQueueRow.css';

const TYPE_LABELS = {
  approval: 'Approval',
  kpi: 'KPI',
  actuals: 'Actuals',
  attendance: 'Attendance',
  'action-item': 'Action item',
  win: 'Win'
};

function getTone(severity) {
  if (severity === 'critical') return 'behind';
  if (severity === 'warn') return 'watch';
  if (severity === 'good') return 'good';
  return 'neutral';
}

function resolvePerson(item) {
  if (!item) return null;
  if (item.personName || item.personEmail || item.personUserId) {
    return {
      name: item.personName,
      email: item.personEmail,
      userId: item.personUserId
    };
  }

  if (item.submittedByUserEmail || item.submittedByUserId) {
    return {
      email: item.submittedByUserEmail,
      userId: item.submittedByUserId
    };
  }

  if (item.ownerUserId || item.ownerEmail || item.managerName) {
    return {
      name: item.managerName || item.ownerEmail,
      email: item.ownerEmail,
      userId: item.ownerUserId
    };
  }

  if (item.type === 'action-item' && item.subtitle) {
    const parts = String(item.subtitle).split('·').map(part => part.trim()).filter(Boolean);
    const name = parts[parts.length - 1];
    return name ? { name } : null;
  }

  if (item.type === 'approval' && /submitted by/i.test(item.subtitle || '')) {
    const email = String(item.subtitle).split(/submitted by/i).pop()?.trim();
    return email ? { email } : null;
  }

  return null;
}

function FocusQueueRow({
  item,
  onCta,
  onDismiss,
  dismissing = false
}) {
  if (!item) return null;
  const typeLabel = TYPE_LABELS[item.type] || item.type || 'Focus';
  const cta = item.cta || {};
  const person = resolvePerson(item);

  return (
    <article className={`focus-queue-row focus-queue-row--${item.type || 'unknown'}${person ? ' focus-queue-row--has-avatar' : ''}`}>
      <div className="focus-queue-row__signal">
        <StatusDot tone={getTone(item.severity)} size="lg" aria-hidden />
      </div>
      {person ? (
        <Avatar
          email={person.email}
          identifier={person.userId || person.email || person.name || item.itemKey}
          name={person.name}
          size="sm"
          userId={person.userId}
        />
      ) : null}
      <div className="focus-queue-row__body">
        <div className="focus-queue-row__meta-line">
          <span className={`focus-queue-row__type focus-queue-row__type--${item.type || 'unknown'}`}>
            {typeLabel}
          </span>
          {item.meta ? <span className="focus-queue-row__meta">{item.meta}</span> : null}
        </div>
        <h3>{item.title}</h3>
        {item.subtitle ? <p>{item.subtitle}</p> : null}
      </div>
      <div className="focus-queue-row__actions">
        {cta.path ? (
          <Button size="sm" variant="secondary" onClick={() => onCta?.(cta.path, item)}>
            {cta.label || 'Open'}
          </Button>
        ) : null}
        <button
          type="button"
          className="focus-queue-row__dismiss"
          onClick={() => onDismiss?.(item)}
          disabled={dismissing}
          aria-label={`Dismiss ${item.title}`}
          title="Dismiss for today"
        >
          ×
        </button>
      </div>
    </article>
  );
}

export default FocusQueueRow;
